import React, { useEffect, useRef, useState } from "react";

function AnimatedNumber({
    value,
    decimals = 0,
    suffix = "",
    locale = "tr-TR",
    duration = 380
}) {
    const target = Number(value || 0);

    const [displayValue, setDisplayValue] = useState(target);

    const previousValueRef = useRef(target);
    const initializedRef = useRef(false);

    useEffect(() => {
        // İlk render'da animasyon yapma.
        if (!initializedRef.current) {
            initializedRef.current = true;
            previousValueRef.current = target;
            setDisplayValue(target);
            return undefined;
        }

        const from = Number(previousValueRef.current || 0);
        const to = target;

        if (from === to) {
            setDisplayValue(to);
            return undefined;
        }

        let frameId;
        const startedAt = performance.now();

        const animate = (now) => {
            const elapsed = now - startedAt;
            const progress = Math.min(1, elapsed / duration);

            // easeOutCubic
            const eased =
                1 - Math.pow(1 - progress, 3);

            const next =
                from + (to - from) * eased;

            setDisplayValue(next);

            if (progress < 1) {
                frameId =
                    requestAnimationFrame(animate);
            } else {
                setDisplayValue(to);
                previousValueRef.current = to;
            }
        };

        frameId =
            requestAnimationFrame(animate);

        return () => {
            if (frameId) {
                cancelAnimationFrame(frameId);
            }
        };
    }, [target, duration]);

    return (
        <>
            {displayValue.toLocaleString(locale, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            })}
            {suffix}
        </>
    );
}

export default function MainKpiGrid({
    overview,
    summary,
    lang,
    canSee,
    productionPeriod = "30d",
    onProductionPeriodChange,
    currentLang = "tr",
    loading = false
}) {
    const kpi = overview?.kpi || {};


    const numberLocale =
        currentLang === "tr"
            ? "tr-TR"
            : "en-US";

    const cards = [
        {
            key: "stat_oee",
            label: lang.dashboard.factoryOee,
            numericValue:
                Number(kpi.factory_oee || 0),
            hasValue: Boolean(overview?.kpi),
            decimals: 1,
            suffix: "%",
            description: lang.dashboard.oeeDesc,
            color: "#22c55e",
            icon: "▥",
        },
        {
            key: "stat_production",
            label: lang.dashboard.totalProduction,
            numericValue:
                Number(kpi.total_production || 0),
            hasValue: Boolean(overview?.kpi),
            decimals: 0,
            suffix: "",
            periodSelector: true,
            description: lang.dashboard.productionDesc,
            color: "#10b981",
            icon: "◇",
        },
        {
            key: "stat_alarms",
            label: lang.dashboard.activeAlarms,
            numericValue:
                Number(kpi.active_alarms || 0),
            hasValue: Boolean(overview?.kpi),
            decimals: 0,
            suffix: "",
            description: lang.dashboard.alarmDesc,
            color: "#f59e0b",
            icon: "!",
        },
        {
            key: "stat_loss",
            label: lang.dashboard.monthlyLoss,
            numericValue:
                Number(summary?.monthly_loss_total || 0),
            hasValue: Boolean(summary),
            decimals: 0,
            suffix: " ₺",
            description: lang.dashboard.lossDesc,
            color: "#ef4444",
            icon: "↘",
        },
    ];

    if (loading) {
        return (
            <div
                className="opement-main-kpi-grid"
            style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(4, minmax(0, 1fr))",
                    gap: 20,
                    alignItems: "start",
                    marginBottom: 20
                }}
            >
                {[0, 1, 2, 3].map(index => (
                    <div
                        key={index}
                        style={{
                            position: "relative",
                            minHeight: 132,
                            padding: "15px 18px",
                            borderRadius: 12,
                            background:
                                "rgba(15,23,42,0.72)",
                            border:
                                "1px solid rgba(148,163,184,0.10)",
                            overflow: "hidden"
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                inset: "0 0 auto 0",
                                height: 2,
                                background:
                                    "rgba(148,163,184,0.16)"
                            }}
                        />

                        <div
                            style={{
                                width: "42%",
                                height: 9,
                                marginTop: 2,
                                borderRadius: 999,
                                background:
                                    "rgba(148,163,184,0.12)"
                            }}
                        />

                        <div
                            style={{
                                width: "58%",
                                height: 29,
                                marginTop: 18,
                                borderRadius: 8,
                                background:
                                    "rgba(148,163,184,0.14)"
                            }}
                        />

                        <div
                            style={{
                                width: "72%",
                                height: 9,
                                marginTop: 14,
                                borderRadius: 999,
                                background:
                                    "rgba(148,163,184,0.09)"
                            }}
                        />

                        {index === 1 && (
                            <>
                                <div
                                    style={{
                                        width: "100%",
                                        height: 1,
                                        marginTop: 18,
                                        background:
                                            "rgba(148,163,184,0.08)"
                                    }}
                                />

                                {[0, 1, 2].map(row => (
                                    <div
                                        key={row}
                                        style={{
                                            marginTop: 10
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent:
                                                    "space-between",
                                                gap: 10
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "42%",
                                                    height: 7,
                                                    borderRadius: 999,
                                                    background:
                                                        "rgba(148,163,184,0.10)"
                                                }}
                                            />

                                            <div
                                                style={{
                                                    width: "22%",
                                                    height: 7,
                                                    borderRadius: 999,
                                                    background:
                                                        "rgba(148,163,184,0.10)"
                                                }}
                                            />
                                        </div>

                                        <div
                                            style={{
                                                width: "100%",
                                                height: 5,
                                                marginTop: 5,
                                                borderRadius: 999,
                                                background:
                                                    "rgba(148,163,184,0.08)"
                                            }}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(4, minmax(0, 1fr))",
                gap: 20,
                alignItems: "start",
                marginBottom: 20,
            }}
        >
            {cards
                .filter(card => canSee(card.key))
                .map(card => (
                    <div
                        key={card.key}

                        onMouseEnter={(event) => {
                            const el = event.currentTarget;

                            el.style.transform =
                                "translateY(-2px)";

                            el.style.borderColor =
                                `${card.color}40`;

                            el.style.boxShadow =
                                `0 13px 30px rgba(2,6,23,0.20), 0 0 0 1px ${card.color}10`;
                        }}

                        onMouseLeave={(event) => {
                            const el = event.currentTarget;

                            el.style.transform =
                                "translateY(0)";

                            el.style.borderColor =
                                "rgba(148,163,184,0.13)";

                            el.style.boxShadow =
                                "0 8px 22px rgba(2,6,23,0.08)";
                        }}

                        style={{
                            position: "relative",
                            minHeight: 132,
                            padding: "15px 58px 15px 18px",
                            borderRadius: 12,

                            background:
                                "rgba(15,23,42,0.72)",

                            border:
                                "1px solid rgba(148,163,184,0.13)",

                            boxShadow:
                                "0 8px 22px rgba(2,6,23,0.08)",

                            overflow: "hidden",

                            transition:
                                "transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease",

                            willChange:
                                "transform, box-shadow"
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                inset: "0 0 auto 0",
                                height: 2,
                                background: card.color,
                                opacity: 0.68,
                            }}
                        />

                        <div
                            style={{
                                position: "absolute",
                                top: 15,
                                right: 16,
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `${card.color}16`,
                                border: `1px solid ${card.color}28`,
                                color: card.color,
                                fontSize: 16,
                                fontWeight: 800,
                                lineHeight: 1,
                                boxShadow: `0 0 20px ${card.color}0D`
                            }}
                        >
                            {card.icon}
                        </div>

                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "#7890b4",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8
                                }}
                            >
                                <span>{card.label}</span>

                                {card.periodSelector && (
                                    <select
                                        value={productionPeriod}
                                        onChange={(e) =>
                                            onProductionPeriodChange?.(
                                                e.target.value
                                            )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: 106,
                                            height: 26,
                                            padding: "0 7px",
                                            borderRadius: 7,
                                            border:
                                                "1px solid rgba(148,163,184,0.22)",
                                            background:
                                                "rgba(15,23,42,0.92)",
                                            color: "#9fb0c9",
                                            fontSize: 10,
                                            fontWeight: 600,
                                            outline: "none",
                                            cursor: "pointer",
                                            textTransform: "none",
                                            letterSpacing: 0
                                        }}
                                    >
                                        <option value="today">
                                            {currentLang === "tr"
                                                ? "Bugün"
                                                : "Today"}
                                        </option>

                                        <option value="yesterday">
                                            {currentLang === "tr"
                                                ? "Dün"
                                                : "Yesterday"}
                                        </option>

                                        <option value="week">
                                            {currentLang === "tr"
                                                ? "Bu Hafta"
                                                : "This Week"}
                                        </option>

                                        <option value="month">
                                            {currentLang === "tr"
                                                ? "Bu Ay"
                                                : "This Month"}
                                        </option>

                                        <option value="30d">
                                            {currentLang === "tr"
                                                ? "Son 30 Gün"
                                                : "Last 30 Days"}
                                        </option>
                                    </select>
                                )}
                            </div>
                        </div>

                        <div
                            style={{
                                marginTop: 10,
                                fontSize: 29,
                                lineHeight: 1,
                                fontWeight: 800,
                                color: card.color,
                            }}
                        >
                            {card.hasValue ? (
                                <AnimatedNumber
                                    value={card.numericValue}
                                    decimals={card.decimals}
                                    suffix={card.suffix}
                                    locale={numberLocale}
                                />
                            ) : (
                                "—"
                            )}
                        </div>

                        <div
                            style={{
                                marginTop: 10,
                                fontSize: 11,
                                color: "#617395",
                            }}
                        >
                            {card.description}
                        </div>

                        
                    </div>
                ))}
        </div>
    );
}
